import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  CheckCircle2,
  Clock3,
  Edit2,
  Eye,
  EyeOff,
  FileCode,
  Gift,
  Globe2,
  Languages,
  Link2,
  Lock,
  Mail,
  MapPin,
  Save,
  ShieldCheck,
  Sparkles,
  UserRound,
  WalletCards,
  X,
  Zap,
} from 'lucide-react';
import { formatPrice, formatDate } from '../utils/helpers';
import { changePassword, resetPassword, setUser, updateUserProfile } from '../store/slices/authSlice';
import Button from '../components/common/Button';
import PointsHistoryModal from '../components/modals/PointsHistoryModal';
import PointsTopUpModal from '../components/modals/PointsTopUpModal';
import TelegramDirectBindModal from '../components/modals/TelegramDirectBindModal';
import { database } from '../services/firebase';
import { ref, onValue } from 'firebase/database';
import { COUNTRIES } from '../utils/countries';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { bindTelegramMiniApp, fetchTelegramBindLink, fetchTelegramBindStatus, unbindTelegramAccount } from '../services/telegramBindingService';
import { getAuthRedirectPath } from '../utils/authRedirect';
import { isTelegramMiniApp, openTelegramBotChat, openTelegramLink } from '../utils/telegramUtils';
import { LANGUAGES, REVERSE_LANGUAGE_MAP, LANGUAGE_MAP } from '../utils/constants';
import { fetchProducts, selectAllProducts } from '../store/slices/productsSlice';

const heroShellClass = 'relative overflow-hidden rounded-3xl border border-primary-100 bg-gradient-to-br from-white via-primary-50 to-amber-50 text-gray-950 shadow-sm dark:border-white/10 dark:bg-gray-950 dark:bg-none dark:text-white dark:shadow-xl';
const heroGlowClass = 'absolute inset-0 bg-gradient-to-br from-primary-500/10 via-accent-500/10 to-amber-400/15 dark:from-primary-500/30 dark:via-accent-500/25 dark:to-amber-400/10';
const heroChipClass = 'mb-2 inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-white/80 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-700 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:text-primary-100 dark:shadow-none';
const heroButtonClass = 'rounded-2xl border border-primary-100 bg-white/80 text-primary-700 hover:bg-white dark:border-white/20 dark:bg-white/10 dark:text-white dark:hover:bg-white/20';
const heroEditPanelClass = 'mt-4 rounded-3xl border border-primary-100 bg-white/80 p-3 text-gray-950 shadow-sm backdrop-blur dark:border-white/10 dark:bg-white/[0.08] dark:text-white dark:shadow-lg sm:p-4';
const heroStatCardClass = 'rounded-2xl border border-primary-100 bg-white/70 px-3 py-2.5 shadow-sm dark:border-white/10 dark:bg-white/[0.08] dark:shadow-none';

const Profile = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { user, isAuthenticated, loading } = useSelector((state) => state.auth);
  const { settings } = useSelector((state) => state.settings);
  const allProducts = useSelector(selectAllProducts);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.displayName || user?.name || '',
    telegramUsername: user?.telegramUsername || '',
    country: user?.country || '',
    language: user?.language || 'English',
  });
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [showDirectBindModal, setShowDirectBindModal] = useState(false);
  const [productAccess, setProductAccess] = useState([]);
  const [loginLimit, setLoginLimit] = useState({ count: 0, max: 30 });
  const [telegramLink, setTelegramLink] = useState('');
  const [telegramBotUsername, setTelegramBotUsername] = useState('');
  const [telegramBound, setTelegramBound] = useState(Boolean(user?.telegramChatId));
  const [telegramBoundChatId, setTelegramBoundChatId] = useState(user?.telegramChatId || '');
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramRefreshing, setTelegramRefreshing] = useState(false);
  const [telegramUnbinding, setTelegramUnbinding] = useState(false);
  const [passwordResetLoading, setPasswordResetLoading] = useState(false);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswordFields, setShowPasswordFields] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    if (isEditing) return;
    const currentLabel = REVERSE_LANGUAGE_MAP[i18n.language] || 'English';
    setFormData((prev) => (
      prev.language === currentLabel ? prev : { ...prev, language: currentLabel }
    ));
  }, [i18n.language, isEditing]);

  useEffect(() => {
    if (allProducts.length === 0) {
      dispatch(fetchProducts());
    }
  }, [allProducts.length, dispatch]);

  useEffect(() => {
    if (!user?.uid) return;
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);

    const userRef = ref(database, `Users/${user.uid}`);
    const unsubscribe = onValue(userRef, (snapshot) => {
      const data = snapshot.exists() ? snapshot.val() : {};
      const accessNode = data?.ProductAccess || {};
      const activeAccess = Object.entries(accessNode)
        .map(([productId, entry]) => ({
          productId,
          ...entry,
        }))
        .filter((entry) => String(entry.status || 'active').toLowerCase() === 'active')
        .sort((left, right) => new Date(right.updatedAt || right.grantedAt || 0) - new Date(left.updatedAt || left.grantedAt || 0));

      setProductAccess(activeAccess);
      setLoginLimit({
        count: data?.Login_Count || 0,
        max: data?.Max_login || 30,
      });
    });

    return () => {
      unsubscribe();
      clearInterval(timer);
    };
  }, [user?.uid]);

  useEffect(() => {
    setTelegramBound(Boolean(user?.telegramChatId));
    setTelegramBoundChatId(user?.telegramChatId || '');
  }, [user?.telegramChatId]);

  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    const loadLink = async () => {
      setTelegramLoading(true);
      try {
        const linkData = await fetchTelegramBindLink();
        if (cancelled) return;
        setTelegramLink(linkData?.deepLink || '');
        setTelegramBotUsername(linkData?.botUsername || '');
      } catch (error) {
        if (!cancelled) {
          console.warn('Failed to load Telegram bind link', error);
        }
      } finally {
        if (!cancelled) {
          setTelegramLoading(false);
        }
      }
    };

    loadLink();
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  const productById = React.useMemo(() => {
    return new Map((allProducts || []).map((product) => [String(product.id), product]));
  }, [allProducts]);

  if (!isAuthenticated) return <Navigate to={getAuthRedirectPath()} replace />;

  const totalSpent = user?.totalSpent ?? 0;
  const rewardPoints = user?.rewardPoints ?? 0;
  const pointsRate = settings?.pointsRate || 20;
  const rewardValue = Math.floor(rewardPoints / pointsRate);
  const telegramUsername = user?.telegramUsername || '';
  const displayName = user?.displayName || user?.name || 'User';
  const profileInitial = (displayName || user?.email || 'U').charAt(0).toUpperCase();
  const joinedLabel = user?.createdAt ? formatDate(user.createdAt) : t('common.not_available');
  const countryLabel = user?.country || t('common.not_available');
  const languageLabel = user?.language || 'English';
  const loginMax = Math.max(Number(loginLimit.max) || 1, 1);
  const loginCount = Number(loginLimit.count) || 0;
  const loginPercent = Math.min(100, Math.round((loginCount / loginMax) * 100));
  const activeAccessPreview = productAccess.slice(0, 3);
  const telegramChatIdMasked = telegramBoundChatId
    ? `${String(telegramBoundChatId).slice(0, 4)}***${String(telegramBoundChatId).slice(-3)}`
    : '';
  const localTimeLabel = (() => {
    try {
      return new Intl.DateTimeFormat(i18n.language || 'en', {
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
        timeZone: user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).format(currentTime);
    } catch (error) {
      return currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  })();

  const statCards = [
    {
      label: t('profile.total_spent'),
      value: formatPrice(totalSpent, 'USDT'),
      detail: t('profile.order_summary'),
      icon: WalletCards,
      tone: 'from-emerald-500 to-teal-500',
    },
    {
      label: t('profile.reward_points'),
      value: rewardPoints,
      detail: `approx ${rewardValue} USDT`,
      icon: Gift,
      tone: 'from-amber-500 to-orange-500',
      action: () => setShowTopUpModal(true),
    },
    {
      label: t('header.my_scripts', 'My Scripts'),
      value: productAccess.length,
      detail: t('my_scripts.manage_access', 'Active licenses'),
      icon: FileCode,
      tone: 'from-primary-500 to-secondary-500',
    },
    {
      label: t('profile.login_limit', 'Login Limit'),
      value: `${loginCount}/${loginLimit.max}`,
      detail: `${loginPercent}% used`,
      icon: Zap,
      tone: 'from-accent-500 to-fuchsia-500',
    },
  ];

  const detailRows = [
    { label: t('auth.email_label'), value: user?.email || t('common.not_available'), icon: Mail },
    { label: t('profile.telegram_label'), value: telegramUsername || t('common.not_available'), icon: Link2 },
    { label: t('profile.country_label'), value: countryLabel, icon: MapPin },
    { label: t('profile.language_label'), value: languageLabel, icon: Languages, noTranslate: true },
  ];

  const formatAccessExpiry = (entry) => {
    if (!entry?.expiresAt || entry.expiresAt === 'lifetime') return t('orders.lifetime', 'Lifetime');
    const date = new Date(entry.expiresAt);
    return Number.isNaN(date.getTime()) ? t('common.not_available') : date.toLocaleDateString();
  };

  const resolveAccessProductName = (entry) => {
    const product = productById.get(String(entry?.productId || ''));
    return (
      product?.name ||
      product?.Name ||
      entry?.productName ||
      entry?.ProductName ||
      entry?.name ||
      t('my_scripts.product_access', 'Product access')
    );
  };

  const heroFieldClass = 'w-full rounded-2xl border border-primary-100 bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 outline-none transition focus:border-amber-300 focus:ring-4 focus:ring-amber-300/20 dark:border-white/10 dark:bg-white/95';
  const panelClass = 'rounded-3xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900 sm:p-5';

  const handleEdit = () => {
    setFormData({
      name: user?.displayName || user?.name || '',
      telegramUsername: user?.telegramUsername || '',
      country: user?.country || '',
      language: user?.language || 'English',
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      name: user?.displayName || user?.name || '',
      telegramUsername: user?.telegramUsername || '',
      country: user?.country || '',
      language: user?.language || 'English',
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      return;
    }

    await dispatch(updateUserProfile({
      name: formData.name.trim(),
      telegramUsername: formData.telegramUsername.trim(),
      country: formData.country,
      language: formData.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }));

    setIsEditing(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (name === 'language') {
      const code = LANGUAGE_MAP[value];
      if (code) {
        i18n.changeLanguage(code);
      }
    }
  };

  const handleOpenTelegramBind = async () => {
    setTelegramLoading(true);
    try {
      if (isTelegramMiniApp()) {
        const bindResult = await bindTelegramMiniApp();
        const nextChatId = bindResult?.chatId || '';
        const nextUsername = bindResult?.telegramUsername || user?.telegramUsername || '';

        setTelegramBound(Boolean(nextChatId));
        setTelegramBoundChatId(nextChatId);
        const nextBotUsername = String(bindResult?.botUsername || telegramBotUsername || '').trim();
        if (nextBotUsername) {
          setTelegramBotUsername(nextBotUsername);
        }
        if (nextUsername) {
          setFormData((prev) => ({ ...prev, telegramUsername: nextUsername }));
        }
        dispatch(setUser({
          ...(user || {}),
          telegramChatId: nextChatId,
          telegramBoundAt: bindResult?.telegramBoundAt || Date.now(),
          telegramUsername: nextUsername,
        }));
        toast.success('Telegram linked successfully. You will now get order updates in Telegram.');
        if (nextBotUsername) {
          setTimeout(() => {
            openTelegramBotChat(nextBotUsername);
          }, 250);
        }
        return;
      }

      let deepLink = String(telegramLink || '').trim();
      let botUsername = String(telegramBotUsername || '').trim();

      if (!deepLink) {
        try {
          const linkData = await fetchTelegramBindLink();
          deepLink = String(linkData?.deepLink || '').trim();
          botUsername = String(linkData?.botUsername || '').trim();

          if (deepLink) {
            setTelegramLink(deepLink);
          }
          if (botUsername) {
            setTelegramBotUsername(botUsername);
          }
        } catch (fetchErr) {
          console.warn('Failed to fetch bind link from worker:', fetchErr);
        }
      }

      if (!deepLink && botUsername) {
        deepLink = `https://t.me/${botUsername}?start=${encodeURIComponent(user?.uid || '')}`;
      }

      if (!deepLink) {
        deepLink = `https://t.me/PSHStoreBot?start=${encodeURIComponent(user?.uid || '')}`;
      }

      openTelegramLink(deepLink);
    } catch (error) {
      const message = String(error?.message || '').trim();
      toast.error(message || 'Telegram bind link is not ready yet. Please retry in a moment.');
    } finally {
      setTelegramLoading(false);
    }
  };

  const handleUnbindTelegram = async () => {
    if (!window.confirm(t('profile.telegram_unbind_confirm', 'Are you sure you want to unlink your Telegram account? You will no longer receive order updates via Telegram.'))) {
      return;
    }

    setTelegramUnbinding(true);
    try {
      await unbindTelegramAccount();
      setTelegramBound(false);
      setTelegramBoundChatId('');
      toast.success(t('profile.telegram_unbind_success', 'Telegram account unlinked successfully!'));
    } catch (error) {
      console.error('Failed to unlink Telegram account:', error);
      toast.error(error?.message || t('profile.telegram_unbind_error', 'Failed to unlink Telegram account'));
    } finally {
      setTelegramUnbinding(false);
    }
  };

  const handleRefreshTelegramStatus = async () => {
    setTelegramRefreshing(true);
    try {
      const status = await fetchTelegramBindStatus();
      const bound = Boolean(status?.bound);
      const nextChatId = status?.chatId || '';
      setTelegramBound(bound);
      setTelegramBoundChatId(nextChatId);
      if (bound && nextChatId) {
        dispatch(setUser({
          ...(user || {}),
          telegramChatId: nextChatId,
        }));
      }
      if (bound) {
        toast.success('Telegram linked successfully. You will now get order updates in Telegram.');
      } else {
        toast.error('Not linked yet. Open the bind link and press Start in Telegram first.');
      }
    } catch (error) {
      toast.error('Failed to check Telegram bind status.');
    } finally {
      setTelegramRefreshing(false);
    }
  };

  const handlePasswordReset = async () => {
    const email = String(user?.email || '').trim();

    if (!email) {
      toast.error('No email address is available for this account.');
      return;
    }

    setPasswordResetLoading(true);
    try {
      await dispatch(resetPassword(email)).unwrap();
    } finally {
      setPasswordResetLoading(false);
    }
  };

  const handlePasswordFieldChange = (event) => {
    const { name, value } = event.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordFieldVisibility = (field) => {
    setShowPasswordFields((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleChangePassword = async () => {
    const currentPassword = passwordForm.currentPassword;
    const newPassword = passwordForm.newPassword;
    const confirmPassword = passwordForm.confirmPassword;

    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('Please fill in all password fields.');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Your new password must be at least 6 characters long.');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match.');
      return;
    }

    if (currentPassword === newPassword) {
      toast.error('Your new password must be different from the current password.');
      return;
    }

    setPasswordChangeLoading(true);
    try {
      await dispatch(changePassword({
        currentPassword,
        newPassword,
      })).unwrap();
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setShowPasswordFields({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
      });
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const renderPasswordInput = (field, label, placeholder, autoComplete) => (
    <div>
      <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {label}
      </label>
      <div className="relative">
        <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          type={showPasswordFields[field] ? 'text' : 'password'}
          name={field}
          value={passwordForm[field]}
          onChange={handlePasswordFieldChange}
          className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-10 text-sm font-medium text-gray-900 outline-none transition focus:border-primary-400 focus:bg-white focus:ring-4 focus:ring-primary-500/10 dark:border-gray-700 dark:bg-gray-950 dark:text-white dark:focus:bg-gray-900"
          placeholder={placeholder}
          autoComplete={autoComplete}
        />
        <button
          type="button"
          onClick={() => togglePasswordFieldVisibility(field)}
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          aria-label={showPasswordFields[field] ? t('profile.hide_password', 'Hide password') : t('profile.show_password', 'Show password')}
        >
          {showPasswordFields[field] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50/80 py-4 dark:bg-gray-950 sm:py-6">
      <div className="container mx-auto px-3 sm:px-4">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-auto max-w-5xl space-y-4"
        >
          <section className={heroShellClass}>
            <div className={heroGlowClass} />
            <div className="relative p-4 sm:p-5">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  {user?.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={displayName}
                      className="h-16 w-16 rounded-3xl border border-primary-100 object-cover shadow-lg dark:border-white/20"
                    />
                  ) : (
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-3xl bg-gradient-to-br from-primary-500 via-accent-500 to-amber-400 text-2xl font-black shadow-lg shadow-primary-900/30">
                      {profileInitial}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className={heroChipClass}>
                      <Sparkles className="h-3.5 w-3.5 text-amber-300" />
                      {t('profile.settings_title')}
                    </div>
                    <h1 className="truncate text-2xl font-black tracking-normal sm:text-3xl">
                      {displayName}
                    </h1>
                    <p className="mt-1 truncate text-sm font-medium text-gray-600 dark:text-gray-300">{user?.email}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  {isEditing ? (
                    <>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSave}
                        loading={loading}
                        icon={Save}
                        disabled={!formData.name.trim()}
                        className="rounded-2xl"
                      >
                        {t('profile.save_button')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancel}
                        icon={X}
                        className={heroButtonClass}
                      >
                        {t('profile.cancel_button')}
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleEdit}
                      icon={Edit2}
                      className={heroButtonClass}
                    >
                      {t('profile.edit_button')}
                    </Button>
                  )}
                </div>
              </div>

              {isEditing && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={heroEditPanelClass}
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-primary-600 dark:text-amber-200">
                        Profile Editor
                      </p>
                      <h2 className="mt-1 text-base font-black text-gray-950 dark:text-white">
                        {t('profile.personal_info')}
                      </h2>
                    </div>
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary-50 text-primary-700 dark:bg-white/10 dark:text-white">
                      <Edit2 className="h-[18px] w-[18px]" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                    <div className="md:col-span-2">
                      <label className="mb-1.5 block text-[10px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        {t('profile.name_label')} *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={heroFieldClass}
                        placeholder="Enter your name"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[10px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        {t('profile.telegram_label')}
                      </label>
                      <input
                        type="text"
                        name="telegramUsername"
                        value={formData.telegramUsername}
                        onChange={handleInputChange}
                        className={heroFieldClass}
                        placeholder="@username"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-[10px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        {t('profile.country_label')}
                      </label>
                      <select
                        name="country"
                        value={formData.country}
                        onChange={handleInputChange}
                        className={heroFieldClass}
                      >
                        <option value="" disabled>Select Country</option>
                        {COUNTRIES.map((country) => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="mb-1.5 block text-[10px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-300">
                        {t('profile.language_label')}
                      </label>
                      <select
                        name="language"
                        value={formData.language}
                        onChange={handleInputChange}
                        className={`${heroFieldClass} notranslate`}
                        translate="no"
                      >
                        {LANGUAGES.map((language) => (
                          <option key={language} value={language}>{language}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end gap-2 md:col-span-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleSave}
                        loading={loading}
                        icon={Save}
                        disabled={!formData.name.trim()}
                        className="flex-1 rounded-2xl"
                      >
                        {t('profile.save_button')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancel}
                        icon={X}
                        className={heroButtonClass}
                      >
                        {t('profile.cancel_button')}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}

              <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4">
                {[
                  { label: 'Joined', value: joinedLabel, icon: Calendar },
                  { label: t('profile.country_label'), value: countryLabel, icon: MapPin },
                  { label: t('profile.language_label'), value: languageLabel, icon: Globe2, noTranslate: true },
                  { label: 'Local Time', value: localTimeLabel, icon: Clock3, noTranslate: true },
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className={heroStatCardClass}>
                      <div className="mb-1 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        <Icon className="h-3.5 w-3.5" />
                        {item.label}
                      </div>
                      <p className="truncate text-xs font-bold text-gray-950 dark:text-white notranslate" translate={item.noTranslate ? 'no' : undefined}>
                        {item.value}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {statCards.map((stat) => {
              const Icon = stat.icon;
              const content = (
                <>
                  <div className={`mb-3 flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br ${stat.tone} text-white shadow-sm`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{stat.label}</p>
                  <p className="mt-1 truncate text-xl font-black text-gray-900 dark:text-white notranslate" translate="no">{stat.value}</p>
                  <p className="mt-1 truncate text-xs font-medium text-gray-500 dark:text-gray-400 notranslate" translate="no">{stat.detail}</p>
                </>
              );

              return stat.action ? (
                <button
                  key={stat.label}
                  onClick={stat.action}
                  className="rounded-3xl border border-gray-100 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md dark:border-gray-800 dark:bg-gray-900"
                >
                  {content}
                </button>
              ) : (
                <div key={stat.label} className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                  {content}
                </div>
              );
            })}
          </section>

          <section className="grid grid-cols-1 gap-4 lg:grid-cols-[1.08fr_0.92fr]">
            <div className={panelClass}>
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-black uppercase tracking-widest text-primary-600 dark:text-primary-400">
                    {t('profile.personal_info')}
                  </p>
                  <h2 className="mt-1 text-lg font-black text-gray-900 dark:text-white">
                    Identity Summary
                  </h2>
                </div>
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-50 text-primary-600 dark:bg-primary-900/30 dark:text-primary-300">
                  <UserRound className="h-5 w-5" />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {detailRows.map((row) => {
                  const Icon = row.icon;
                  return (
                    <div key={row.label} className="rounded-2xl border border-gray-100 bg-gray-50 px-3 py-3 dark:border-gray-800 dark:bg-gray-950">
                      <div className="mb-1 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-gray-500 dark:text-gray-400">
                        <Icon className="h-3.5 w-3.5" />
                        {row.label}
                      </div>
                      <p className="truncate text-sm font-bold text-gray-900 dark:text-white notranslate" translate={row.noTranslate ? 'no' : undefined}>
                        {row.value}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={panelClass}>
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-black uppercase tracking-widest text-sky-600 dark:text-sky-400">
                    {t('profile.telegram_notifications_title', 'Telegram Notifications')}
                  </p>
                  <h2 className="mt-1 text-lg font-black text-gray-900 dark:text-white">
                    {telegramBound ? t('profile.telegram_linked', 'Linked') : t('profile.telegram_not_linked', 'Not Linked')}
                  </h2>
                </div>
                <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${
                  telegramBound
                    ? 'bg-green-50 text-green-600 dark:bg-green-900/25 dark:text-green-300'
                    : 'bg-amber-50 text-amber-600 dark:bg-amber-900/25 dark:text-amber-300'
                }`}>
                  {telegramBound ? <CheckCircle2 className="h-5 w-5" /> : <Link2 className="h-5 w-5" />}
                </div>
              </div>

              <div className="space-y-3">
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-950">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-bold text-gray-500 dark:text-gray-400">{t('profile.telegram_status', 'Status')}</span>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-black ${
                      telegramBound
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                    }`}>
                      {telegramBound ? t('profile.telegram_linked', 'Linked') : t('profile.telegram_not_linked', 'Not Linked')}
                    </span>
                  </div>
                  {telegramBound && telegramChatIdMasked && (
                    <p className="mt-2 truncate text-xs font-medium text-gray-500 dark:text-gray-400">
                      {t('profile.telegram_bound_chat_id', 'Bound chat id')}: {telegramChatIdMasked}
                    </p>
                  )}
                  {telegramBotUsername && (
                    <p className="mt-1 truncate text-xs font-medium text-gray-500 dark:text-gray-400">
                      Bot: @{telegramBotUsername}
                    </p>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleOpenTelegramBind}
                    loading={telegramLoading}
                    icon={Link2}
                    className="rounded-2xl"
                  >
                    {telegramBound ? t('profile.telegram_rebind_button', 'Rebind Telegram') : t('profile.telegram_bind_button', 'Bind Telegram')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDirectBindModal(true)}
                    className="rounded-2xl"
                  >
                    {t('profile.telegram_manual_link_button', 'Enter Chat ID')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRefreshTelegramStatus}
                    loading={telegramRefreshing}
                    className="rounded-2xl"
                  >
                    {t('profile.telegram_check_status_button', 'Check Status')}
                  </Button>
                  {telegramBound && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUnbindTelegram}
                      loading={telegramUnbinding}
                      className="rounded-2xl text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                    >
                      {t('profile.telegram_unbind_button', 'Unlink')}
                    </Button>
                  )}
                </div>

                <p className="rounded-2xl bg-sky-50 px-3 py-2 text-xs font-medium text-sky-700 dark:bg-sky-900/20 dark:text-sky-200">
                  {isTelegramMiniApp()
                    ? t('profile.telegram_steps_inline', 'Inside Telegram, tap Bind Telegram once and it will link instantly. Outside Telegram, use the bot link and then tap Check Status.')
                    : t('profile.telegram_steps', 'Steps: tap Bind Telegram -> open bot -> press Start -> return and tap Check Status.')}
                </p>
              </div>
            </div>
          </section>

          <section className="grid grid-cols-1 gap-4 xl:grid-cols-[1.18fr_0.82fr]">
            <div className={panelClass}>
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-black uppercase tracking-widest text-gray-500 dark:text-gray-400">
                    {t('profile.password_reset_title', 'Password & Security')}
                  </p>
                  <h2 className="mt-1 text-lg font-black text-gray-900 dark:text-white">
                    {t('profile.change_password_button', 'Change Password')}
                  </h2>
                </div>
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                  <ShieldCheck className="h-5 w-5" />
                </div>
              </div>

              <div className="mb-4 flex flex-col gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-950 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{t('auth.email_label')}</p>
                  <p className="truncate text-sm font-bold text-gray-900 dark:text-white">{user?.email || t('common.not_available')}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePasswordReset}
                  loading={passwordResetLoading}
                  icon={Mail}
                  disabled={!user?.email}
                  className="rounded-2xl"
                >
                  {t('profile.password_reset_button', 'Send Reset Link')}
                </Button>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                {renderPasswordInput(
                  'currentPassword',
                  t('profile.current_password_label', 'Current Password'),
                  t('profile.current_password_placeholder', 'Enter current password'),
                  'current-password'
                )}
                {renderPasswordInput(
                  'newPassword',
                  t('profile.new_password_label', 'New Password'),
                  t('profile.new_password_placeholder', 'Enter new password'),
                  'new-password'
                )}
                {renderPasswordInput(
                  'confirmPassword',
                  t('profile.confirm_new_password_label', 'Confirm New Password'),
                  t('profile.confirm_new_password_placeholder', 'Confirm new password'),
                  'new-password'
                )}
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <p className="max-w-xl text-xs font-medium text-gray-500 dark:text-gray-400">
                  {t('profile.change_password_hint', 'Use your current password to set a new one instantly. If you forgot it, use the reset link above instead.')}
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleChangePassword}
                  loading={passwordChangeLoading}
                  icon={Lock}
                  disabled={!user?.email}
                  className="rounded-2xl"
                >
                  {t('profile.change_password_button', 'Change Password')}
                </Button>
              </div>
            </div>

            <div className={panelClass}>
              <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-black uppercase tracking-widest text-amber-600 dark:text-amber-400">
                    {t('profile.order_summary')}
                  </p>
                  <h2 className="mt-1 text-lg font-black text-gray-900 dark:text-white">
                    Account Activity
                  </h2>
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                <button
                  onClick={() => setShowTopUpModal(true)}
                  className="rounded-2xl bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700 transition-colors hover:bg-emerald-100 dark:bg-emerald-900/25 dark:text-emerald-200 dark:hover:bg-emerald-900/40"
                >
                  {t('profile.buy_points', 'Buy Points')}
                </button>
                <button
                  onClick={() => setShowHistoryModal(true)}
                  className="rounded-2xl bg-amber-50 px-3 py-2 text-xs font-black text-amber-700 transition-colors hover:bg-amber-100 dark:bg-amber-900/25 dark:text-amber-200 dark:hover:bg-amber-900/40"
                >
                  {t('profile.view_history')}
                </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3 dark:border-gray-800 dark:bg-gray-950">
                  <div className="mb-2 flex items-center justify-between text-xs font-bold text-gray-500 dark:text-gray-400">
                    <span>{t('profile.login_limit', 'Login Limit')}</span>
                    <span className="notranslate" translate="no">{loginCount}/{loginLimit.max}</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-800">
                    <div
                      className="h-2 rounded-full bg-gradient-to-r from-primary-500 to-accent-500"
                      style={{ width: `${loginPercent}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-xs font-black uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {t('header.my_scripts', 'My Scripts')}
                    </p>
                    <Link to="/my-scripts" className="text-xs font-bold text-primary-600 hover:text-primary-700 dark:text-primary-400">
                      View all
                    </Link>
                  </div>

                  {activeAccessPreview.length > 0 ? (
                    <div className="space-y-2">
                      {activeAccessPreview.map((entry) => (
                        <div key={entry.productId} className="flex items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-gray-50 px-3 py-2.5 dark:border-gray-800 dark:bg-gray-950">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-bold text-gray-900 dark:text-white" title={resolveAccessProductName(entry)}>
                              {resolveAccessProductName(entry)}
                            </p>
                            <p className="truncate text-xs font-medium text-gray-500 dark:text-gray-400">
                              {formatAccessExpiry(entry)}
                            </p>
                          </div>
                          <span className="rounded-full bg-green-100 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-green-700 dark:bg-green-900/30 dark:text-green-300">
                            Active
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-3 py-5 text-center dark:border-gray-700 dark:bg-gray-950">
                      <FileCode className="mx-auto h-6 w-6 text-gray-400" />
                      <p className="mt-2 text-sm font-bold text-gray-900 dark:text-white">
                        {t('my_scripts.no_scripts', 'No scripts available')}
                      </p>
                      <p className="mt-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                        {t('profile.no_access')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </motion.div>
      </div>

      <PointsHistoryModal
        isOpen={showHistoryModal}
        onClose={() => setShowHistoryModal(false)}
        userId={user?.uid}
      />

      <PointsTopUpModal
        isOpen={showTopUpModal}
        onClose={() => setShowTopUpModal(false)}
      />

      <TelegramDirectBindModal
        isOpen={showDirectBindModal}
        onClose={() => setShowDirectBindModal(false)}
        onSuccess={(result) => {
          const nextChatId = result?.chatId || '';
          const nextUsername = result?.telegramUsername || '';
          setTelegramBound(true);
          setTelegramBoundChatId(nextChatId);
          dispatch(setUser({
            ...(user || {}),
            telegramChatId: nextChatId,
            telegramUsername: nextUsername,
          }));
        }}
        currentChatId={telegramBoundChatId}
        currentUsername={user?.telegramUsername || ''}
      />
    </div>
  );
};

export default Profile;
